import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import ScrollToTop from '@/components/base/ScrollToTop';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';
import { articleImages } from '@/mocks/images';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-20 pb-14 min-h-screen bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[
              { label: '首页', href: '/' },
              { label: '文章未找到' },
            ]} />
            <div className="text-center py-20">
              <div className="w-14 h-14 flex items-center justify-center mx-auto rounded-full bg-background-200 mb-4">
                <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
              </div>
              <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-4">文章未找到</h1>
              <p className="text-foreground-500 mb-6">该文章不存在或已被移除</p>
              <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium">
                返回首页
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = getRelatedArticles(article);
  const imageUrl = articleImages[article.imageSeq] || articleImages['article-1-image'];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    image: imageUrl,
    publisher: {
      '@type': 'Organization',
      name: '心灵家缘',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://xinlingjiayuan.com/' },
      { '@type': 'ListItem', position: 2, name: article.categoryName, item: `https://xinlingjiayuan.com/category/${article.categorySlug}` },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify([articleSchema, breadcrumbSchema]) }}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-20 pb-14 min-h-screen bg-background-50">
        <article className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb items={[
            { label: '首页', href: '/' },
            { label: article.categoryName, href: `/category/${article.categorySlug}` },
            { label: article.title },
          ]} />

          {/* Article Header */}
          <header className="mb-8">
            <Link
              to={`/category/${article.categorySlug}`}
              className="inline-block px-3 py-1 text-xs font-medium text-primary-700 bg-primary-100 rounded-full mb-4 hover:bg-primary-200 transition-colors whitespace-nowrap"
            >
              {article.categoryName}
            </Link>
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-6">
              <span className="whitespace-nowrap">
                <div className="w-4 h-4 inline-flex items-center justify-center mr-1">
                  <i className="ri-user-line"></i>
                </div>
                {article.author}
              </span>
              <span className="whitespace-nowrap">
                <div className="w-4 h-4 inline-flex items-center justify-center mr-1">
                  <i className="ri-calendar-line"></i>
                </div>
                {article.date}
              </span>
              <span className="whitespace-nowrap">
                <div className="w-4 h-4 inline-flex items-center justify-center mr-1">
                  <i className="ri-time-line"></i>
                </div>
                {article.readTime}阅读
              </span>
            </div>

            <div className="w-full h-64 md:h-96 rounded-xl overflow-hidden bg-background-200 mb-8">
              <img
                src={imageUrl}
                alt={article.title}
                title={`${article.title} - 心灵家缘`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </header>

          {/* Article Content */}
          <div
            className="prose-custom font-body text-foreground-800 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* Tags */}
          <div className="mt-10 pt-6 border-t border-background-200">
            <div className="flex flex-wrap gap-2">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 text-xs bg-background-100 text-foreground-600 rounded-full whitespace-nowrap"
                >
                  # {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Author Bio */}
          <div className="mt-8 p-5 bg-background-100 rounded-xl border border-background-200/60 flex items-start gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
              <i className="ri-user-3-line text-xl text-primary-600"></i>
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground-900 mb-1">{article.author}</p>
              <p className="text-xs text-foreground-500">家庭关系咨询师，专注情感边界与家庭和谐研究。</p>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="max-w-7xl mx-auto px-4 md:px-6 mt-14">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
              相关文章
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {relatedArticles.map((ra) => (
                <ArticleCard key={ra.id} article={ra} />
              ))}
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}